import { useEffect } from "react";
import type { ValidationResult } from "../types";

// When a generation response comes back with tier2_pending=true, the
// expert auditors are still running on the server (background waitUntil).
// This hook polls the content's cached GET until the stored validation
// clears tier2_pending (Tier 2 landed) or a bounded number of attempts
// elapses, then writes the resolved result into component state.
//
const POLL_INTERVAL_MS = 2500;
// Hard wall-clock cap. After this we stop polling and show whatever
// Tier-1 results we have, flagged with a note. Kept above the backend's
// Tier-2 timeout so the server can finish and persist before we give up.
const MAX_POLL_MS = 120_000;
// If a stored result is already pending AND older than this, Tier 2 never
// landed (the background job died); don't even start polling.
const STALE_PENDING_MS = 5 * 60_000;
const TIMED_OUT_NOTE = "Full verification timed out.";

// Clear the pending flag and attach the timeout note, keeping the
// Tier-1 issues already on the result. Resolving this into state stops
// the "Verifying…" spinner and surfaces the available findings.
function resolveTimedOut(v: ValidationResult): ValidationResult {
  return { ...v, tier2_pending: false, note: TIMED_OUT_NOTE };
}

// `refetch` should hit the read endpoint that returns the stored
// validation for this content (e.g. api.getTimeline → r.validation).
export function useValidationPoll(
  validation: ValidationResult | null | undefined,
  refetch: () => Promise<ValidationResult | null | undefined>,
  setValidation: (v: ValidationResult | null) => void,
) {
  const pending = !!validation?.tier2_pending;
  useEffect(() => {
    if (!pending || !validation) return;

    // (2) Stale pending: the stored Tier-1 result is already older than
    // 5 minutes, so the background audit is never going to land. Resolve
    // immediately to Tier-1 results instead of polling a dead job.
    const checkedAtMs = Date.parse(validation.checked_at);
    if (
      Number.isFinite(checkedAtMs) &&
      Date.now() - checkedAtMs > STALE_PENDING_MS
    ) {
      setValidation(resolveTimedOut(validation));
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    // Latest fetched result — used for the Tier-1 fallback on timeout.
    let latest: ValidationResult = validation;

    const tick = async () => {
      if (cancelled) return;
      try {
        const next = await refetch();
        if (cancelled) return;
        if (next) {
          latest = next;
          setValidation(next);
          if (!next.tier2_pending) return; // resolved — stop polling
        }
      } catch {
        /* transient — keep trying within the budget */
      }
      if (cancelled) return;
      // (1) 60s hard cap: stop polling, show Tier 1 with a timeout note.
      if (Date.now() - startedAt >= MAX_POLL_MS) {
        setValidation(resolveTimedOut(latest));
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Re-arm only when pending toggles; the loop self-schedules otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);
}
